import { graphql } from "gatsby";
import React, { useEffect, useMemo, useRef, useState } from "react";

import BlogHero from "../../components/blog/blog-hero/blogHero";
import InterestedArticles from "../../components/blog/interested-articles";
import PopupSubscribe from "../../components/blog/popupSubscribe";
import FeaturedSignupForm from "../../components/blog/signup-form/FeaturedSignupForm";
import WhatsupNext from "../../components/blog/whatsup-next";
import Image from "../../components/image";
import Layout from "../../components/layout";
import Newsletter from "../../components/newsletter";
import Seo from "../../components/seo";
import EnquireCard from "../../components/shared/enquireCard";
import Modal from "../../components/shared/modal";
import BlogStickyFooter from "../../components/shared/sticky/blogStickyFooter";
import NewToGvi from "../../components/widgets/newToGvi";
import SubscribeNewsletter from "../../components/widgets/subscribeNewsletter";
import BlogPagePromoContainer from "../../container/blog-page/blogPagePromoContainer";
import { ThemeContext, useTheme } from "../../context/ThemeContext";
import { useBannerDetails } from "../../hooks/useBannerManager";
import { shortcodePreProcessor } from "../../utils/formatContent";

const BlogPost = ({ data }) => {
  const { currentSiteMeta } = useTheme();

  const { post, genericDetails } = data;

  const blogPostAcfFields = post?.blogPosts ?? {};
  const { stickyBookDesc } =
    genericDetails?.globalProgramConfig?.acfGenericGlobalConfig ?? {};

  const {
    title: stickyTitle,
    image: stickyImage,
    desc: stickyDesc
  } = useBannerDetails();

  const { relatedArticlesByFocus, author } = post;
  const relatePostsByFocus =
    relatedArticlesByFocus?.nodes
      ?.flatMap((foc) => foc.posts?.nodes)
      ?.filter((filtPost) => filtPost.slug !== post.slug)
      ?.slice(0, 10) ?? [];

  const whatsNextArticle = relatePostsByFocus.shift() ?? null;

  const enquireCardDetails = {
    cardTitle: "",
    enquireCardImg: stickyImage,
    cardDesc:
      `<div>${stickyTitle || ""}</div><div class='font-normal font-body text-sm pt-4'>${stickyDesc || ""}</div>` ??
      "",
    cardBookDesc: stickyBookDesc
  };

  const stickyWidgetDetails = useMemo(() => {
    const gbcLeftFloatingWdgt =
      genericDetails?.globalBlogsConfig?.acfGlobalBlogConfig
        ?.gbcLeftFloatingWdgt;
    const result = gbcLeftFloatingWdgt?.[0] ?? {};
    return result;
  }, [
    genericDetails?.globalBlogsConfig?.acfGlobalBlogConfig?.gbcLeftFloatingWdgt
  ]);

  const { gbcNewsletterPopUpWdgt } =
    genericDetails?.globalBlogsConfig?.acfGlobalBlogConfig ?? {};

  const newsletterPopupDetails = gbcNewsletterPopUpWdgt?.shift() ?? [];

  const stickyFooterDetails = useMemo(() => {
    const gbcStickyFooterWdgt =
      genericDetails?.globalBlogsConfig?.acfGlobalBlogConfig
        ?.gbcStickyFooterWdgt;
    return gbcStickyFooterWdgt?.[0] ?? {};
  }, [
    genericDetails?.globalBlogsConfig?.acfGlobalBlogConfig?.gbcStickyFooterWdgt
  ]);

  const footerImage =
    post?.blogFooterStickyBannerImage?.sourceUrl ??
    stickyFooterDetails?.gbcStickyFooterWdgtImage?.sourceUrl;
  const footerTitle =
    post?.blogFooterStickyBannerTitle ??
    stickyFooterDetails?.gbcStickyFooterWdgtTitle;
  const footerSubtitle =
    post?.blogFooterStickyBannerSummary ??
    stickyFooterDetails?.gbcStickyFooterWdgtSubtitle;
  const footerCtaTitle =
    post?.blogFooterStickyCtaTitle ??
    stickyFooterDetails?.gbcStickyFooterWdgtCtaTitle;

  const scholarships =
    genericDetails?.globalProgramConfig?.acfGenericGlobalConfig
      ?.exclusiveDiscount ?? [];

  const [openModal, setOpenModal] = useState(false);
  const [newsletterIsSubscribed, setNewsletterIsSubscribed] = useState(false);

  const intArticlesRef = useRef(null);
  const scrollTrigger = useRef(0);

  useEffect(() => {
    scrollTrigger.current = intArticlesRef?.current?.offsetTop;
  }, []);

  useEffect(() => {
    const isSubscribed = JSON.parse(
      typeof window !== "undefined"
        ? window.localStorage.getItem("isBlogPopupNewsletterSubscribed")
        : false
    );

    if (isSubscribed || newsletterIsSubscribed) return;

    const newletterPopupScroll = function () {
      const currentPos = window.scrollY;
      if (!newsletterIsSubscribed && currentPos > scrollTrigger.current) {
        if (!openModal) {
          setOpenModal(true);
        }
      }
    };

    window.addEventListener("scroll", newletterPopupScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", newletterPopupScroll);
    };
  }, [newsletterIsSubscribed]);

  const handleCloseModal = () => {
    setOpenModal(false);
    setNewsletterIsSubscribed(true);

    if (typeof window !== "undefined") {
      window.localStorage.setItem("isBlogPopupNewsletterSubscribed", true);
    }
  };

  const containerRef = useRef(null);
  const stickyRef = useRef(null);
  const contentHtmlRef = useRef(null);
  const [showFloating, setShowFloating] = useState(false);

  const authorName = author?.node?.firstName
    ? [author?.node?.firstName, author?.node?.lastName].join(" ")
    : "";

  const authorAvatar = author?.node?.avatar?.url;
  const authorDesc = author?.node?.description ?? "";

  useEffect(() => {
    const handleScroll = () => {
      const toppoint = containerRef.current?.getBoundingClientRect().top;
      const bottompoint = containerRef.current?.getBoundingClientRect().bottom;
      const cardTop = (window.innerHeight - 410) / 2;
      const cardHeight = 410;

      if (window.scrollY > contentHtmlRef.current?.clientHeight / 2) {
        setShowFloating(true);
      } else {
        setShowFloating(false);
      }
      if (stickyRef.current) {
        if (toppoint > cardTop) {
          stickyRef.current.style.top = 0;
          stickyRef.current.style.bottom = "auto";
        } else if (bottompoint < cardHeight + cardTop) {
          stickyRef.current.style.top = "auto";
          stickyRef.current.style.bottom = 0;
        } else {
          stickyRef.current.style.top = cardTop - toppoint + "px";
          stickyRef.current.style.bottom = "auto";
        }
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [stickyRef, containerRef]);

  const shortcodeConfig = {
    blogPostAcfFields,
    scholarships,
    currentSiteMeta
  };

  const postContent = shortcodePreProcessor({
    ...shortcodeConfig,
    description: post?.content
  });

  return (
    <ThemeContext.Consumer>
      {(theme) => (
        <Layout
          pageTitle={post?.seoLegacy?.title ?? post?.seo?.title ?? post.title}
          containerClass={"w-screen mx-auto relative"}
          brandingOpts={{ ...theme }}
          updateOnScroll={false}
        >
          <BlogHero post={post} />
          <div className="pt-28 sm:pt-4 dark:bg-gray-darker">
            <div className="container sm:w-[80%] w-full px-4 sm:px-0 mx-auto block sm:flex justify-between">
              <div className="flex flex-col w-full sm:w-4/5 sm:pr-8 sm:py-28 tab-content">
                <div
                  ref={contentHtmlRef}
                  className="w-full leading-normal tab-content_html text-black-lighter dark:text-gray-generic sm:pb-20"
                  dangerouslySetInnerHTML={{ __html: postContent }}
                />

                {authorDesc && (
                  <div className="outer-container border border-black-lighter dark:border-white border-x-0 py-[4px] mt-10 sm:mt-0">
                    <div className="flex items-center justify-start border sm:min-h-40 border-black-lighter dark:border-white border-x-0">
                      {authorAvatar && authorName && (
                        <div className="self-start w-1/3 h-full pt-6 mr-3 avatar-img sm:w-1/5">
                          <Image
                            imageAlt={`Article by ${authorName} `}
                            image={""}
                            imageClass="self-start w-20 rounded-xl"
                            sourceUrl={authorAvatar}
                          />
                        </div>
                      )}

                      <div className="flex flex-col justify-center dark:text-gray-darken text-black-lighter h-full py-6 w-2/3 sm:w-4/5 sm:min-h-[150px]">
                        {authorName && (
                          <p className="pb-2 text-xl dark:font-light dark:text-white text-black-lighter">
                            By {authorName}
                          </p>
                        )}
                        <div
                          className="text-xs text-black-lighter dark:text-gray-generic sm:flex-1"
                          dangerouslySetInnerHTML={{ __html: authorDesc }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {blogPostAcfFields?.blogLsuTitle && (
                  <div className="relative block w-full py-20 sm:mb-32">
                    <FeaturedSignupForm post={blogPostAcfFields} />
                  </div>
                )}
              </div>

              <div className="flex-col hidden sm:w-1/5 sm:flex">
                <div className="widget-sidebar">
                  <NewToGvi />
                  {/* <ReadMoreAbout post={post} /> */}
                  <SubscribeNewsletter />
                </div>
                <div className="relative h-full">
                  <div className="w-full md:absolute md:h-full md:top-0 md:right-0 md:flex md:pb-8">
                    <div className="relative w-full" ref={containerRef}>
                      <div
                        ref={stickyRef}
                        className={`card-sticky-container relative sm:absolute`}
                      >
                        <EnquireCard
                          applyNow="/application-form"
                          enquireCardDetails={enquireCardDetails}
                          cardStyle="sm:!w-full"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {stickyWidgetDetails &&
              Object.keys(stickyWidgetDetails).length > 0 && (
                <div className="blog-promo-wrapper fixed left-0 top-[50%] translate-x-0 -translate-y-1/2 z-50">
                  <BlogPagePromoContainer
                    isShow={showFloating}
                    setShowFloating={setShowFloating}
                    stickyWidgetDetails={stickyWidgetDetails}
                    scholarships={scholarships}
                    post={blogPostAcfFields}
                  />
                </div>
              )}

            {stickyFooterDetails &&
              Object.keys(stickyFooterDetails).length > 0 && (
                <div className="sm:block hidden fixed bottom-0 left-0 w-full dark:bg-white dark:bg-opacity-20 bg-gray-lighten bg-opacity-60 z-30 backdrop-blur-[20px]">
                  <BlogStickyFooter
                    stickyFooterDetails={stickyFooterDetails}
                    footerImage={footerImage}
                    footerTitle={footerTitle}
                    footerSubtitle={footerSubtitle}
                    footerCtaTitle={footerCtaTitle}
                    post={blogPostAcfFields}
                    enquireBtn={false}
                  />
                </div>
              )}

            {whatsNextArticle && <WhatsupNext article={whatsNextArticle} />}
            {relatePostsByFocus.length > 0 && (
              <div ref={intArticlesRef}>
                <InterestedArticles posts={relatePostsByFocus} />
              </div>
            )}

            <Newsletter />

            {openModal && (
              <Modal
                closeModal={handleCloseModal}
                modalContentStyles="mx-auto py-3 w-full h-full sm:w-[40vw] sm:h-[40vw]"
                modalContainerStyles="w-full sm:w-9/12"
                modalCloseBtnStyles="!w-full flex justify-end"
                modalContent={
                  <div className="w-full h-full">
                    <PopupSubscribe
                      newsletterPopupDetails={newsletterPopupDetails}
                      handleCloseModal={handleCloseModal}
                    />
                  </div>
                }
              />
            )}
          </div>
        </Layout>
      )}
    </ThemeContext.Consumer>
  );
};

// export async function config() {
// return ({ params }) => {
//   return {
//     defer: true
//   };
// };
// }

export const Head = ({ data }) => {
  const post = data?.post ?? {};
  const authorName = post?.author?.node?.firstName
    ? [post?.author?.node?.firstName, post?.author?.node?.lastName].join(" ")
    : "";
  const blogDetails = {
    url: post?.slug,
    title: post?.seoLegacy?.title ?? post?.seo?.title ?? post.title,
    image:
      post?.featuredImage?.node?.sourceUrl ??
      post?.globalConfig?.backgroundImage?.sourceUrl ??
      null,
    dateCreated: post?.publishedDate,
    dateModified: "",
    description: post?.seoLegacy?.description ?? post?.seo?.description ?? "",
    author: authorName
  };
  return (
    <Seo
      title={post?.seoLegacy?.title ?? post?.seo?.title ?? post.title}
      description={post?.seoLegacy?.description ?? post?.seo?.description ?? ""}
      lang={post?.locale ?? ""}
      translations={post?.translations ?? ""}
      image={
        post?.featuredImage?.node?.sourceUrl ??
        post?.globalConfig?.backgroundImage?.sourceUrl ??
        null
      }
      blogDetails={blogDetails}
      meta={[
        {
          name: "robots",
          content: post?.seoLegacy?.robots ?? post?.seo?.robots
        }
      ]}
    />
  );
};

export const postQuery = graphql`
  query ($slug: String!) {
    post: wpPost(slug: { eq: $slug }) {
      ...featuredEditorial
      publishedDate: date(formatString: "MMMM D, YYYY")
      content
      authorDatabaseId
      authorId
      author {
        node {
          avatar {
            url
            default
          }
          name
          firstName
          lastName
          description
        }
      }
      tags {
        nodes {
          slug
          uri
          name
          link
          id
        }
      }
      seo {
        ...seoFragment
      }
      ...WpPost_SeolegacyFragment
      geographical {
        nodes {
          name
          parentId
          id
          uri
        }
      }
      translations {
        locale
        post_title
        id
        href
      }
      relatedArticlesByFocus: taxonomyBlogFocuses {
        nodes {
          posts {
            nodes {
              ...featuredEditorial
              publishedDate: date(formatString: "MMMM D, YYYY")
              author {
                node {
                  avatar {
                    url
                    default
                  }
                  name
                  firstName
                  lastName
                  description
                }
              }
            }
          }
        }
      }
      blogPosts {
        blogWnxtArticle {
          ...featuredEditorial
        }
      }
    }

    genericDetails: wp {
      ...WpSharedFragment
    }
  }
`;

export default BlogPost;
